import React, { useEffect, useState, useCallback } from 'react'
import DocumentHeader from '../../../../documents/components/DocumentHeader'
import { request } from '../../../../../../utils/request';
import { useForm } from 'react-hook-form';
import { FormProvider } from 'react-hook-form';
import { Box, ToggleButton, ToggleButtonGroup, Card, Typography, Alert } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import InvoiceDatesSection from '../../../../documents/components/InvoiceDatesSection';
import NotesTermsSection from '../../../../documents/components/NotesTermsSection';
import AmountSummarySection from '../../../../documents/components/AmountSummarySection';
import ItemsTableSection from '../../../../documents/components/ItemsTableSection';
import { GstType, PurchaseFormData, TaxBreakdownItem } from '../../../../types';
import { useGstMode } from '../../../../../../utils/useGstMode';
import { toast } from 'react-toastify';
import { useFinancialYear } from '../../../../../../utils/useFinancialYear';
import { useSearchParams, useLocation } from 'react-router-dom';
import VendorSelectSection from '../../../../documents/components/VendorSelectSection';

const CreatePurchaseInvoice = () => {
  const [searchParams] = useSearchParams();
  const financialYear = useFinancialYear();
  const defaultPurchaseNumber = `PC${financialYear}-1`
  const navigate = useNavigate();
  const location = useLocation();
  const { gstEnabled } = useGstMode();
  const { id } = useParams();
  const isEditMode = !!id;

  const [taxableAmount, setTaxableAmount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [, setTotalTax] = useState(0);
  const [, setTotalDiscount] = useState(0);
  const [, setItems] = useState<any[]>([]);
  const [taxBreakdown, setTaxBreakdown] = useState<TaxBreakdownItem[]>([]);
  const [nextPurchaseNumber, setNextPurchaseNumber] = useState(defaultPurchaseNumber);
  const [isLoadingPurchaseNumber, setIsLoadingPurchaseNumber] = useState(false);
  const [loading, setLoading] = useState(false);

  const queryGstType = searchParams.get('gstType');

  // Determine default GST type based on query parameter or gstEnabled
  const getDefaultGstType = (): GstType => {
    if (queryGstType === 'NON-GST') {
      return 'NON-GST';
    }
    return gstEnabled ? 'GST' : 'GST';
  };

  const methods = useForm<PurchaseFormData>({
    defaultValues: {
      purchaseType: 'PURCHASE',
      gstType: getDefaultGstType(), // Use the function to get default
      party: '',
      vendor: '',
      purchaseNumber: '',
      purchaseDate: new Date().toISOString().split('T')[0],
      date: new Date().toISOString().split('T')[0],
      dueDate: '',
      paymentTerms: '',
      items: [],
      charges: [],
      discount: undefined,
      notes: '',
      terms: '',
      roundOff: false,
      totalAmount: 0,
      taxableAmount: 0,
      subtotal: 0,
      receivedAmount: 0,
      balanceAmount: 0,
      taxBreakdown: [],
    },
    mode: 'onChange'
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    getValues,
    reset
  } = methods;

  // Watch gstType value
  const gstType = watch('gstType', getDefaultGstType());
  const purchaseType = watch('purchaseType', 'PURCHASE');

  // Function to fetch next invoice number from API
  const fetchNextPurchaseNumber = useCallback(async () => {
    if (isEditMode) return; // Don't fetch for edit mode

    setIsLoadingPurchaseNumber(true);
    try {
      const res = await request({
        url: 'purchase/get-next-purchase-number',
        method: "get",
        params: { purchaseType: purchaseType, gstType: gstType }
      });

      if (res.success && res.purchaseNumber) {
        const newPurchaseNumber = res.purchaseNumber;
        setNextPurchaseNumber(newPurchaseNumber);
        setValue('purchaseNumber', newPurchaseNumber);
      }
    } catch (error) {
      console.error("Error fetching next invoice number:", error);
      // Fallback: Use default
      setNextPurchaseNumber(`PC${financialYear}-1`);
      setValue('purchaseNumber', `PC${financialYear}-1`);
    } finally {
      setIsLoadingPurchaseNumber(false);
    }
  }, [financialYear, gstType, purchaseType, isEditMode, setValue]);

  // Function to fetch existing invoice data for edit mode
  const fetchInvoiceData = useCallback(async () => {
    if (!isEditMode) return;

    setLoading(true);
    try {
      const res = await request({
        url: `purchase/get-single-purchase-invoice/${id}`,
        method: "get"
      });

      if (res.success && res.data) {
        const invoiceData = res.data;

        // Format dates to YYYY-MM-DD
        const formatDate = (dateString: string | Date) => {
          if (!dateString) return new Date().toISOString().split('T')[0];
          const date = new Date(dateString);
          return date.toISOString().split('T')[0];
        };

        // Get party ID - handle both object and string
        let partyId = '';
        let vendorId = '';
        if (typeof invoiceData.party === 'object' && invoiceData.party !== null) {
          partyId = invoiceData.party._id || '';
        } else if (typeof invoiceData.party === 'string') {
          partyId = invoiceData.party;
        }

        if (typeof invoiceData.vendor === 'object' && invoiceData.vendor !== null) {
          vendorId = invoiceData.vendor._id || '';
        } else if (typeof invoiceData.vendor === 'string') {
          vendorId = invoiceData.vendor;
        }

        // Prepare form data
        const formData = {
          purchaseType: invoiceData.purchaseType || 'PURCHASE',
          gstType: invoiceData.gstType || 'GST',
          paymentType: invoiceData.paymentType,
          party: partyId || '',
          vendor: vendorId || '',
          purchaseNumber: invoiceData.purchaseNumber || '',
          purchaseDate: formatDate(invoiceData.purchaseDate),
          date: formatDate(invoiceData.date),
          dueDate: formatDate(invoiceData.dueDate),
          paymentTerms: invoiceData.paymentTerms || '',
          items: invoiceData.items || [],
          charges: invoiceData.charges || [],
          discount: invoiceData.discount || undefined,
          notes: invoiceData.notes || '',
          terms: invoiceData.terms || '',
          roundOff: invoiceData.roundOff || false,
          totalAmount: invoiceData.totalAmount || 0,
          taxableAmount: invoiceData.taxableAmount || 0,
          subtotal: invoiceData.subtotal || 0,
          receivedAmount: invoiceData.receivedAmount || 0,
          balanceAmount: invoiceData.balanceAmount || 0,
          taxBreakdown: invoiceData.taxBreakdown || [],
        };

        // Reset form with fetched data
        reset(formData);

        // Update state values
        setTaxableAmount(invoiceData.taxableAmount || 0);
        setTotalAmount(invoiceData.totalAmount || 0);
        setTotalTax(0); // You might need to calculate this from items
        setTotalDiscount(0); // You might need to calculate this from items
        setItems(invoiceData.items || []);
        setTaxBreakdown(invoiceData.taxBreakdown || []);

      } else {
        toast.error(res.message || "Failed to load invoice data");
      }
    } catch (error: any) {
      console.error("Error fetching invoice data:", error);
      toast.error(error?.message || "Failed to load invoice data");
    } finally {
      setLoading(false);
    }
  }, [id, isEditMode, reset]);

  // Function to update all items tax to "none" when NON-GST is selected
  const updateItemsTaxToNone = useCallback(() => {
    const currentItems = getValues('items') || [];
    if (currentItems.length === 0) return;

    const updatedItems = currentItems.map((item: any) => ({
      ...item,
      tax: {
        type: 'none',
        rate: 0
      },
      amount: ((item.quantity || 1) * (item.price || 0)) - (item.discount?.amount || 0)
    }));

    setItems(updatedItems);
    setValue('items', updatedItems);
    setTaxBreakdown([]);
    setValue('taxBreakdown', []);
  }, [getValues, setValue]);

  // Effect to handle query parameter for GST type (only for create mode)
  useEffect(() => {
    if (!isEditMode && queryGstType === 'NON-GST') {
      setValue('gstType', 'NON-GST');
      // Update items tax to none for NON-GST
      updateItemsTaxToNone();
    }
  }, [isEditMode, queryGstType, setValue, updateItemsTaxToNone]);

  // Initial data fetch
  useEffect(() => {
    if (isEditMode) {
      fetchInvoiceData();
    } else {
      fetchNextPurchaseNumber();
    }
  }, [isEditMode, fetchInvoiceData, fetchNextPurchaseNumber]);

  // Update next invoice number when invoice type changes (only for create mode)
  useEffect(() => {
    if (!isEditMode) {
      fetchNextPurchaseNumber();
    }
  }, [purchaseType, isEditMode, fetchNextPurchaseNumber]);

  const requestWrapper = ({ url, method, params }: { url: string; method: string; params?: any }) => {
    return request({ method: method as any, url, params });
  }

  // Handle GST type change
  const handleGstTypeChange = (event: React.MouseEvent<HTMLElement>, newGstType: GstType | null) => {
    if (newGstType !== null) {
      setValue('gstType', newGstType);
      if (newGstType === 'NON-GST') {
        updateItemsTaxToNone();
      }
    }
  };

  // Effect to handle GST type changes
  useEffect(() => {
    if (gstType === 'NON-GST') {
      updateItemsTaxToNone();
    }
  }, [gstType, updateItemsTaxToNone]);

  const onSubmit = async (data: PurchaseFormData) => {

    try {
      const state = location.state as { source?: string; quotationId?: string };
      // Prepare the payload
      const payload = {
        ...data,
        party: data.party && data.party.trim() !== '' ? data.party : null,
        vendor: data.vendor && data.vendor.trim() !== '' ? data.vendor : null,
        purchaseNumber: isEditMode ? data.purchaseNumber : (data.purchaseNumber || undefined),
        ...(state?.source === 'quotation' && state?.quotationId && {
          convertedFromQuotationId: state.quotationId
        })
      };

      const apiUrl = isEditMode
        ? `purchase/update-purchase-invoice/${id}`
        : 'purchase/create-purchase-invoice';
      const method = isEditMode ? 'put' : 'post';

      const res = await request({
        url: apiUrl,
        method: method,
        data: payload
      });

      if (res.success) {
        const successMessage = isEditMode
          ? "Invoice Updated Successfully"
          : "Invoice Created Successfully";
        toast.success(res.message || successMessage);

        // Navigate to invoices list
        navigate('/accounts/purchases/purchaseInvoice');
      } else {
        toast.error(res.message || "Operation failed");
      }
    } catch (error: any) {
      console.error("Error submitting invoice:", error);
      toast.error(error || 'Something want wrong');
    }
  }

  const handleCancel = () => {
    navigate('/accounts/purchases/purchaseInvoice');
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Typography>Loading invoice data...</Typography>
      </Box>
    );
  }

  return (
    <>
      <DocumentHeader
        heading={isEditMode ? 'Edit Purchase Invoice' : 'Create Purchase Invoice'}
        showBackButton
        showCancelButton={true}
        showSaveButton={true}
        onCancel={handleCancel}
        onSave={handleSubmit(onSubmit)}
        saveButtonText={isEditMode ? 'Update Purchase' : 'Create Purchase'}
        saveDisabled={isSubmitting}
      />

      {/* GST Type Selection Section */}
      {!gstEnabled && (
        <Card sx={{
          mb: 2,
          p: 2,
          borderRadius: 1,
          boxShadow: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1" fontWeight="medium">
              Purchase Type:
            </Typography>

            <ToggleButtonGroup
              value={gstType}
              exclusive
              onChange={handleGstTypeChange}
              aria-label="GST Type"
              size="small"
              color="primary"
              disabled={isEditMode} // Disable in edit mode
            >
              <ToggleButton value="GST" aria-label="GST">
                GST
              </ToggleButton>
              <ToggleButton value="NON-GST" aria-label="NON-GST">
                Non-GST
              </ToggleButton>
            </ToggleButtonGroup>

            <Typography variant="caption" color={gstType === 'GST' ? "success.main" : "info.main"}>
              {gstType === 'GST'
                ? 'Includes GST tax calculations'
                : 'No GST tax calculations'}
            </Typography>
          </Box>
          {isEditMode && (
            <Alert severity="info" sx={{ mt: 1, fontSize: '0.875rem' }}>
              GST type cannot be changed for existing purchase.
            </Alert>
          )}
        </Card>
      )}

      {/* Form Provider */}
      <FormProvider {...methods}>
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Box display={'flex'} gap={1} sx={{ alignItems: 'stretch' }}>
            <Box sx={{ flex: 1, height: 'auto' }}>

              <VendorSelectSection
                request={requestWrapper}
                apiParams={{ status: 'active' }}
                billToLabel="Bill From"
                partyFormName="party"
                vendorFormName="vendor"
                onAddNewVendor={() => navigate('/vendors/vendor/addVendor')}
                showTotalDue={true}
                compact={true}
              />

              {errors.vendor && (
                <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                  Please select a vendor/Party
                </Typography>
              )}
            </Box>
            <Box sx={{ flex: 1 }}>
              <InvoiceDatesSection
                invoiceNumberLabel="Purchase Invoice No"
                invoiceDateLabel="Purchase Invoice Date"
                paymentTermsLabel="Payment Terms"
                dueDateLabel="Due Date"
                formFieldNames={{
                  invoiceNumber: 'purchaseNumber',
                  invoiceDate: 'purchaseDate',
                  paymentTerms: 'paymentTerms',
                  dueDate: 'dueDate'
                }}
                autoGenerateInvoiceNumber={!isEditMode}
                nextInvoiceNumber={nextPurchaseNumber}
                showPaymentTerms={true}
                showDueDate={true}
                compact={true}
                disabled={isLoadingPurchaseNumber || isEditMode} // Disable invoice number in edit mode
                onInvoiceDateChange={() => { }}
                onPaymentTermsChange={() => { }}
                onDueDateChange={() => { }}
                paymentTermsOptions={[
                  { value: 'immediate', label: 'Immediate', days: 0 },
                  { value: 'net7', label: 'Net 7 Days', days: 7 },
                  { value: 'net15', label: 'Net 15 Days', days: 15 },
                  { value: 'net30', label: 'Net 30 Days', days: 30 },
                  { value: 'net45', label: 'Net 45 Days', days: 45 },
                  { value: 'net60', label: 'Net 60 Days', days: 60 },
                ]}
              />
              {isLoadingPurchaseNumber && !isEditMode && (
                <Typography variant="caption" color="text.secondary">
                  Loading next invoice number...
                </Typography>
              )}
              {nextPurchaseNumber && !isEditMode && (
                <Typography variant="caption" color="primary">
                  Next available number: {nextPurchaseNumber}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Items Table Section */}
          <Box>
            <ItemsTableSection
              formFieldName="items"
              onItemsChange={(updatedItems) => {
                setItems(updatedItems);
                setValue('items', updatedItems);
              }}
              onSubtotalChange={() => { }}
              onTotalDiscountChange={(discount) => {
                setTotalDiscount(discount);
              }}
              onTotalTaxChange={(tax) => {
                setTotalTax(tax);
              }}
              onTaxableAmountChange={(taxable) => {
                setTaxableAmount(taxable);
                setValue('taxableAmount', taxable);
              }}
              onTotalAmountChange={(total) => {
                setTotalAmount(total);
                setValue('totalAmount', total);
              }}
              onTaxBreakdownChange={(breakdown) => {
                if (gstType === 'GST') {
                  setTaxBreakdown(breakdown);
                  setValue('taxBreakdown', breakdown);
                }
              }}
              showHsnNo={gstType === 'GST'}
              showDiscount={true}
              showTax={gstType === 'GST'}
              disabled={false}
              compact={true}
              isPurchase={false}
              maxItems={100}
            // Items will be automatically populated from the form
            />
          </Box>

          <Box display={'flex'} gap={1} sx={{ alignItems: "stretch" }}>
            <Box sx={{ flex: 1 }}>
              <NotesTermsSection
                notesLabel="Additional Notes"
                termsLabel="Terms & Conditions"
                addNoteButtonText="Add Note"
                addTermsButtonText="Add Terms"
                formFieldNames={{
                  notes: 'notes',
                  terms: 'terms'
                }}
                showNotes={true}
                showTerms={true}
                allowMultipleNotes={false}
                allowMultipleTerms={false}
                compact={true}
                maxNotesLength={500}
                maxTermsLength={1000}
                onNotesChange={() => { }}
                onTermsChange={() => { }}
                notesPlaceholder="Add any special instructions, delivery notes, or additional information..."
                termsPlaceholder="Add payment terms, return policies, warranties, or other conditions..."
                defaultExpanded={!!getValues('notes') || !!getValues('terms')}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <AmountSummarySection
                taxableAmount={taxableAmount}
                totalAmount={totalAmount}
                taxBreakdown={gstType === 'GST' ? taxBreakdown : []}
                balanceAmountLabel='Balance Amount'
                receivedAmountLabel='Amount Paid'
                formFieldNames={{
                  charges: 'charges',
                  discount: 'discount',
                  roundOff: 'roundOff',
                  totalAmount: 'totalAmount',
                  taxableAmount: 'taxableAmount',
                  subtotal: 'subtotal',
                  paymentType: 'paymentType',
                  receivedAmount: 'receivedAmount',
                  taxBreakdown: 'taxBreakdown',
                  balanceAmount: 'balanceAmount'
                }}
                compact={true}
                taxRate={gstType === 'GST' ? 18 : 0}
                showTaxableAmount={gstType === 'GST'}
                onTotalAmountChange={() => { }}
              />
            </Box>
          </Box>
        </Box>
      </FormProvider>
    </>
  )
}

export default CreatePurchaseInvoice;